/* eslint-disable no-useless-escape */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { landingBg, logo } from 'assets/img';
import LandingMarketingAside from '../LandingMarketingAside';
import {
  withApi,
  customTypes,
  useResponsive,
  validate as externalValidator,
  useAuth0,
  useForm,
  useBriteVerify,
} from 'utils';

import {
  Row,
  Col,
  Input,
  Form,
  FormField,
  Button,
  Typography,
  PasswordCheck,
  Dialog,
  DIALOG_TYPES,
  Tooltip,
  Checkbox,
  Link,
  FormFieldIcon,
} from 'components/alf-design';
import Guidance from './Guidance';
import AccountVerificationHelp from './AccountVerificationHelp';
import './CreateAccount.css';
import config from 'config';

const initState = {
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  password: '',
  confirmPassword: '',
  policyNumber: '',
  zip: '',
  checkbox: 'individual',
};

const trimLeadingSpace = (value) => value.replace(/^\s+/, '');

const sanitizePolicyNumberInput = (value) => value.replace(/[^A-Za-z0-9]/g, '');

const formatPolicyNumberForSubmission = (policyNumber) =>
  policyNumber.replace(/\D+$/g, '').toUpperCase();

const isPasswordValid = (password) => {
  if (!password) {
    return false;
  }
  const characterLengthRule = password.length >= 8 && password.length <= 64;
  const uppercaseCharacterRule = /^.*[A-Z].*$/.test(password) ? 1 : 0;
  const lowercaseCharacterRule = /^.*[a-z].*$/.test(password) ? 1 : 0;
  const numberRule = /^.*[0-9].*$/.test(password) ? 1 : 0;
  const symbolRule = /^.*[^0-9a-zA-Z].*$/.test(password) ? 1 : 0;

  const atLeast3True =
    uppercaseCharacterRule + lowercaseCharacterRule + numberRule + symbolRule >=
    3;
  return characterLengthRule && atLeast3True;
};

function validate(values) {
  const errors = {};

  const isIndividual = values.checkbox === 'individual';
  const isCompany = values.checkbox === 'llc';

  if (isIndividual) {
    if (
      !values.firstName ||
      values.firstName.trim().length < 2 ||
      !/^[A-Za-z\s.,'\-]+$/.test(values.firstName)
    ) {
      errors.firstName = 'First name is required.';
    }

    if (
      !values.lastName ||
      values.lastName.trim().length < 2 ||
      !/^[A-Za-z\s.,'\-]+$/.test(values.lastName)
    ) {
      errors.lastName = 'Last name is required.';
    }
  }

  if (isCompany) {
    if (!values.company || values.company.trim().length < 2) {
      errors.company = 'Company/DBA name is required.';
    }
  }

  if (!/.+@.+\..+/.test(values.email)) {
    errors.email = 'Please provide a valid email address.';
  }

  if (!values.policyNumber || values.policyNumber.trim() === '') {
    errors.policyNumber = 'Policy number is required.';
  } else {
    const beginning = values.policyNumber.trim().slice(0, 2).toUpperCase();
    const firstChar = values.policyNumber.trim().charAt(0).toUpperCase();
    const validPrefixes = ['H3', 'H6', 'HO', 'D1'];
    if (config.dp3Enabled) {
      validPrefixes.push('D3');
    }

    if (!validPrefixes.includes(beginning) && firstChar !== 'S') {
      errors.policyNumber = config.dp3Enabled
        ? 'Only policies beginning with H3, H6, HO, D3, D1, or S can create an account at this time.'
        : 'Only policies beginning with H3, H6, HO, D1, or S can create an account at this time.';
    } else if (!externalValidator.policyNumber(values.policyNumber)) {
      errors.policyNumber = 'Invalid policy number.';
    }
  }

  if (!values.zip || !/^\d{5}$/.test(values.zip)) {
    errors.zip = 'Invalid zip.';
  }

  if (!values.password) {
    errors.password = 'A password is required.';
  } else if (!isPasswordValid(values.password)) {
    errors.password = 'Password is too weak.';
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Password confirmation is required.';
  } else if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'The passwords do not match.';
  } else if (errors.password) {
    errors.confirmPassword = 'Password is too weak.';
  }

  return errors;
}

const createAccountFormLabelProps = {
  labelTypographyType: 'h2',
  labelClassName: '!ml-[12px]',
};

const createAccountFormFieldProps = {
  ...createAccountFormLabelProps,
  dense: true,
};

const CreateAccount = ({ api }) => {
  const navigate = useNavigate();
  const [error, setError] = useState({});
  const [verify, setVerify] = useState(false);
  const [, setPasswordValid] = useState(false);
  const [selected, setSelected] = useState('individual');

  const { loginWithRedirect } = useAuth0();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFormValues,
    values,
    errors,
    touched,
    isSubmitting,
  } = useForm(initState, validate, true);
  const { screenIsUp } = useResponsive();

  const onSubmit = (e) => {
    handleSubmit(e, (callback) => {
      const { email, password, firstName, lastName, company } = values;
      const policies = [
        {
          id: 0,
          policyNumber: formatPolicyNumberForSubmission(values.policyNumber),
          zip: values.zip,
        },
      ];
      const body = {
        email,
        password,
        policies,
        ...(selected === 'individual'
          ? { firstName, lastName, company: '' }
          : { firstName: '', lastName: '', company }),
      };

      api
        .signUp(body)
        .then(() => {
          datadogRum.addAction('account.creation.succeeded');
          setVerify(true);
          callback(true);
        })
        .catch((err) => {
          window.scrollTo(0, 0);
          callback(err);
          if (err.response) {
            if (err.response.status === 404) {
              setError({
                number: 404,
                description:
                  'Policy entered was not found. Please contact your agent for assistance.',
              });
            } else if (err.response.status === 400) {
              setError({
                number: 400,
                description:
                  'Uh-Oh! The policy information entered does not match our records.',
              });
            } else {
              setError({
                number: err.response.status,
                description: err.response.data.Errors[0].value,
              });
            }
          } else if (err.request) {
            setError({
              number: null,
              description:
                'Network error. Please check your connection and try again.',
            });
          } else {
            setError({
              number: null,
              description: 'Error attempting to create new user account.',
            });
          }

          datadogRum.addError(err, {
            event_group: 'account.creation.failed',
            event_name:
              err?.response?.status === 400
                ? 'account.creation.policy-info-does-not-match.failed'
                : err?.response?.status === 404
                  ? 'account.creation.policy-not-found.failed'
                  : err?.response
                    ? 'account.creation.api-error.failed'
                    : err?.request
                      ? 'account.creation.network-error.failed'
                      : 'account.creation.unexpected-error.failed',
            http_status: err?.response?.status,
          });

          datadogLogs.logger.error(`Create Account - ${values.policyNumber}`, {
            err,
          });
        });
    });
  };

  const handleLogin = async () => {
    await loginWithRedirect();
    navigate('/', { replace: true });
  };

  const handleCheckbox = (e) => {
    if (selected !== e.target.name) {
      setFormValues({
        checkbox: e.target.name,
        ...(values.firstName !== '' && { firstName: '' }),
        ...(values.lastName !== '' && { lastName: '' }),
        ...(values.company !== '' && { company: '' }),
      });
      setSelected(e.target.name);
    }
  };

  const isFormEmpty = (values) => {
    const {
      checkbox,
      firstName,
      lastName,
      company,
      email,
      password,
      confirmPassword,
      policyNumber,
      zip,
    } = values;

    const requiredFields =
      checkbox === 'individual'
        ? [
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
            policyNumber,
            zip,
          ]
        : [company, email, password, confirmPassword, policyNumber, zip];

    return requiredFields.some((v) => !v || v.trim() === '');
  };

  useEffect(() => {
    document.title =
      'Create an Account | Online Policy Access for Slide Insurance Policyholders';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Create an account and verify your policy to gain online access to your Slide policy. Make payments, view your policy details, access your agent info, and more!',
      );
    }
  }, []);

  const [blurred, setBlurred] = useState({
    email: false,
    password: false,
    confirmPassword: false,
    firstName: false,
    lastName: false,
    company: false,
    zip: false,
    policyNumber: false,
  });

  const markBlurred = (field, e) => {
    setBlurred((prev) => ({ ...prev, [field]: true }));
    handleBlur(e);
  };

  const emailStatus = useBriteVerify(
    values.email,
    touched.email,
    blurred.email,
  );

  const accountForm = (
    <>
      {error.description && (
        <Typography className="error-message">{error.description}</Typography>
      )}
      <Form id="form" onSubmit={onSubmit}>
        <Row gutter={24} className="my-[16px]">
          <Col xs={24}>
            <Typography component="h2" type="h2-create-account">
              Verify Your Existing Policy
            </Typography>
          </Col>
        </Row>
        <Row gutter={24} className="checkbox-row">
          <Col xs={24}>
            <div className="create-account-policy-checkboxes">
              <Checkbox
                id="individual"
                name="individual"
                className="individual"
                checked={selected === 'individual'}
                onChange={handleCheckbox}
              >
                <Typography className="checkbox-caption" type="caption1">
                  Individual
                </Typography>
              </Checkbox>
              <Checkbox
                id="llc"
                name="llc"
                className="llc"
                checked={selected === 'llc'}
                onChange={handleCheckbox}
              >
                <Typography className="checkbox-caption" type="caption1">
                  Company/DBA
                </Typography>
              </Checkbox>
            </div>
          </Col>
        </Row>
        <Row gutter={24} className="mt-[16px] create-account-fields-row">
          <Col>
            <div className="create-account-policy-number-field">
              <FormField
                {...createAccountFormFieldProps}
                label="Policy Number"
                error={blurred.policyNumber ? errors.policyNumber : ''}
                icon={<Guidance />}
              >
                <Input
                  name="policyNumber"
                  type="text"
                  id="policy-input"
                  onChange={(e) => {
                    const nextValue = sanitizePolicyNumberInput(e.target.value);
                    e.target.value = nextValue;
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('policyNumber', e)}
                  value={values.policyNumber}
                  error={blurred.policyNumber && !!errors.policyNumber}
                  success={blurred.policyNumber && !errors.policyNumber}
                />
              </FormField>
            </div>
          </Col>
          {selected === 'individual' ? (
            <>
              <Col>
                <FormField
                  {...createAccountFormFieldProps}
                  label="First Name"
                  error={blurred.firstName ? errors.firstName : ''}
                >
                  <Input
                    name="firstName"
                    id="first-name"
                    onChange={(e) => {
                      const nextValue = trimLeadingSpace(e.target.value);
                      if (/^[A-Za-z\s.,'\-]*$/.test(nextValue)) {
                        e.target.value = nextValue;
                        handleChange(e);
                      }
                    }}
                    onBlur={(e) => markBlurred('firstName', e)}
                    value={values.firstName}
                    error={blurred.firstName && !!errors.firstName}
                    success={blurred.firstName && !errors.firstName}
                  />
                </FormField>
              </Col>
              <Col>
                <FormField
                  {...createAccountFormFieldProps}
                  label="Last Name"
                  error={blurred.lastName ? errors.lastName : ''}
                >
                  <Input
                    name="lastName"
                    id="last-name"
                    onChange={(e) => {
                      const nextValue = trimLeadingSpace(e.target.value);
                      if (/^[A-Za-z\s.,'\-]*$/.test(nextValue)) {
                        e.target.value = nextValue;
                        handleChange(e);
                      }
                    }}
                    onBlur={(e) => markBlurred('lastName', e)}
                    value={values.lastName}
                    error={blurred.lastName && !!errors.lastName}
                    success={blurred.lastName && !errors.lastName}
                  />
                </FormField>
              </Col>
            </>
          ) : (
            <Col>
              <FormField
                {...createAccountFormFieldProps}
                label="Company/DBA Name"
                error={blurred.company ? errors.company : ''}
              >
                <Input
                  name="company"
                  id="company"
                  onChange={(e) => {
                    e.target.value = trimLeadingSpace(e.target.value);
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('company', e)}
                  value={values.company}
                  error={blurred.company && !!errors.company}
                  success={blurred.company && !errors.company}
                />
              </FormField>
            </Col>
          )}
          <Col>
            <FormField
              {...createAccountFormFieldProps}
              label="Zip Code"
              error={
                blurred.zip && errors.zip ? 'Invalid property zip code.' : ''
              }
            >
              <Input
                id="zip-input"
                name="zip"
                type="text"
                onChange={(e) => {
                  const nextValue = trimLeadingSpace(e.target.value);
                  if (/^\d{0,5}$/.test(nextValue)) {
                    e.target.value = nextValue;
                    handleChange(e);
                  }
                }}
                maxLength="5"
                inputMode="numeric"
                onBlur={(e) => markBlurred('zip', e)}
                value={values.zip}
                error={blurred.zip && !!errors.zip}
                success={blurred.zip && !errors.zip}
              />
            </FormField>
          </Col>
        </Row>

        <Row
          gutter={24}
          className="form-row create-account-fields-row mt-[24px]"
        >
          <Col xs={24}>
            <Typography component="h2" type="h2-create-account">
              Create an Account
            </Typography>
          </Col>
        </Row>
        <div className="form-fields">
          <Row gutter={24} className="form-row create-account-fields-row">
            <Col>
              <FormField
                {...createAccountFormFieldProps}
                label="Email Address"
                error={
                  blurred.email && (emailStatus === 'invalid' || errors.email)
                    ? 'Please provide a valid email address.'
                    : ''
                }
                icon={
                  <FormFieldIcon>
                    <Tooltip
                      ariaLabel="Email Tooltip"
                      iconSize={14}
                      text="NOTE: This email is used for account access only. Policy correspondence will be directed to the email address found on your policy declaration page."
                      dataFor="create-account-email-tooltip"
                    />
                  </FormFieldIcon>
                }
              >
                <Input
                  name="email"
                  type="email"
                  id="email-input"
                  onChange={(e) => {
                    const nextValue = trimLeadingSpace(e.target.value);
                    e.target.value = nextValue;
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('email', e)}
                  value={values.email}
                  error={
                    blurred.email && (emailStatus === 'invalid' || errors.email)
                  }
                  success={blurred.email && emailStatus === 'valid'}
                />
              </FormField>
            </Col>
            <Col>
              <FormField
                {...createAccountFormFieldProps}
                label="Password"
                error={blurred.password ? errors.password : ''}
                labelFor="password-input"
              >
                <PasswordCheck
                  password={values.password}
                  setPasswordValid={setPasswordValid}
                  isPopover={screenIsUp('lg')}
                >
                  <Input
                    name="password"
                    type="password"
                    id="password-input"
                    onChange={(e) => {
                      e.target.value = trimLeadingSpace(e.target.value);
                      handleChange(e);
                    }}
                    onBlur={(e) => markBlurred('password', e)}
                    value={values.password}
                    error={blurred.password && !!errors.password}
                    success={blurred.password && !errors.password}
                  />
                </PasswordCheck>
              </FormField>
            </Col>
            <Col>
              <FormField
                {...createAccountFormFieldProps}
                label="Confirm Password"
                error={blurred.confirmPassword ? errors.confirmPassword : ''}
              >
                <Input
                  name="confirmPassword"
                  type="password"
                  id="confirm-password-input"
                  onChange={(e) => {
                    e.target.value = trimLeadingSpace(e.target.value);
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('confirmPassword', e)}
                  value={values.confirmPassword}
                  error={blurred.confirmPassword && !!errors.confirmPassword}
                  success={blurred.confirmPassword && !errors.confirmPassword}
                />
              </FormField>
            </Col>
          </Row>
        </div>
        <Row gutter={16} className="form-button-row">
          <Col xs={24} className="button-container">
            <Button
              id="signup-button-create-account"
              className="create-account-submit"
              withLoading={isSubmitting}
              type="primary"
              htmlType="submit"
              disabled={
                isSubmitting ||
                isFormEmpty(values) ||
                emailStatus !== 'valid' ||
                Object.keys(errors).filter((key) => key !== 'email').length > 0
              }
              fullWidth
            >
              Create Account
            </Button>
          </Col>
        </Row>

        <Link
          href="#"
          id="create-account-login-link"
          className="block text-center mt-0 mb-[24px]"
          onClick={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <Typography type="link" component="span" className="!text-[16px]">
            Already have an account? Log In
          </Typography>
        </Link>

        <div className="sm:hidden">
          <AccountVerificationHelp className="mx-auto" />
        </div>
      </Form>
    </>
  );

  return (
    <>
      <div className="hidden sm:flex w-full px-[108px] pt-[14px] pb-[9px]">
        <img src={logo} alt="Logo" className="w-[116px]" />
      </div>

      <div
        className="create-account-split flex flex-1 flex-col sm:flex-row w-full mx-[24px] sm:mx-0 sm:mr-[24px] sm:ml-[108px] sm:pr-0 pb-8 sm:pb-0 min-h-[calc(100vh-150px)] sm:bg-no-repeat sm:bg-cover"
        style={{
          ['--create-account-landing-bg']: `url(${landingBg})`,
        }}
      >
        <div className="create-account-mobile-header flex justify-center mb-6 pt-6 sm:hidden shrink-0">
          <img src={logo} alt="mySlide" className="h-[40px] w-auto" />
        </div>

        <div className="flex flex-col w-full sm:w-2/5 sm:min-h-[595px] sm:bg-legacy-white sm:items-center sm:pt-[70px] lg:pl-[20px] sm:overflow-y-auto shrink-0">
          <div className="create-account-form-panel w-full max-w-[480px] mx-auto px-4 sm:max-w-[400px] sm:px-5 sm:pb-10">
            {accountForm}
          </div>
        </div>

        <div className="hidden sm:flex sm:flex-col w-3/5 shrink-0 min-h-[595px]">
          <LandingMarketingAside />
          <div className="mt-[40px] mb-[24px] pl-[24px] lg:pl-[128px] pr-[108px]">
            <AccountVerificationHelp />
          </div>
        </div>
      </div>

      <Dialog
        id="verify-email"
        type={DIALOG_TYPES.CONTENT}
        show={verify}
        onClose={handleLogin}
        title="Verify Your Email"
        subtitle="A verification email has been sent to your email address. Please verify your account before you log in."
        primaryButtonText="Back to Log In"
        ariaLabel="Account verification"
      />
    </>
  );
};

CreateAccount.propTypes = {
  api: customTypes.api,
};

export { formatPolicyNumberForSubmission, sanitizePolicyNumberInput };
export default withApi(CreateAccount);
